/**
 * User List Table Component
 * Displays paginated list of users with sorting, filtering, and row actions
 *
 * @module Features/UserManagement/Components
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as ResetPasswordIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { UserListDto } from '../Types';

interface UserListTableProps {
  data: UserListDto[] | undefined;
  page: number;
  pageSize: number;
  totalCount: number;
  isLoading: boolean;
  error?: any;

  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;

  onEdit: (userId: number) => void;
  onDelete: (userId: number) => void;
  onResetPassword: (userId: number) => void;
  onDetails: (userId: number) => void;
}

/**
 * Formats date to readable format
 */
const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * User status chip component
 */
const UserStatusChip: React.FC<{ isActive: boolean; emailConfirmed: boolean }> = ({
  isActive,
  emailConfirmed,
}) => {
  if (!isActive) {
    return <Chip label="Nieaktywne" color="default" size="small" />;
  }

  if (!emailConfirmed) {
    return <Chip label="Niezweryfikowane" color="warning" size="small" />;
  }

  return <Chip label="Aktywne" color="success" size="small" />;
};

/**
 * Role chips component
 */
const RoleChips: React.FC<{ roles: string[] }> = ({ roles }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {roles.map((role) => (
      <Chip
        key={role}
        label={role}
        size="small"
        variant="outlined"
        color={role === 'Admin' ? 'error' : 'primary'}
      />
    ))}
  </Box>
);

/**
 * User List Table Component
 */
export const UserListTable: React.FC<UserListTableProps> = ({
  data,
  page,
  pageSize,
  totalCount,
  isLoading,
  error,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onResetPassword,
  onDetails,
}) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1); // MUI uses 0-based pagination
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  if (error) {
    return (
      <Alert severity="error">
        Nie udało się wczytać użytkowników: {error?.message || 'Nieznany błąd'}
      </Alert>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 1000 }}>
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Imię i nazwisko</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Utworzono</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Akcje
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : data && data.length > 0 ? (
            data.map((user) => (
              <TableRow
                key={user.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell sx={{ fontFamily: 'monospace' }}>{user.email}</TableCell>
                <TableCell>
                  {user.firstName || user.lastName
                    ? `${user.firstName || ''} ${user.lastName || ''}`
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  <RoleChips roles={user.roles} />
                </TableCell>
                <TableCell>
                  <UserStatusChip isActive={user.isActive} emailConfirmed={user.emailConfirmed} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem', color: '#666' }}>
                  {formatDate(user.createdAt)}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    title="Szczegóły"
                    size="small"
                    onClick={() => onDetails(user.id)}
                    color="info"
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    title="Edytuj użytkownika"
                    size="small"
                    onClick={() => onEdit(user.id)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    title="Resetuj hasło"
                    size="small"
                    onClick={() => onResetPassword(user.id)}
                    color="warning"
                  >
                    <ResetPasswordIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    title="Usuń użytkownika"
                    size="small"
                    onClick={() => onDelete(user.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                Nie znaleziono użytkowników
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page - 1} // MUI uses 0-based pagination
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Użytkowników na stronę:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count} użytkowników`}
      />
    </TableContainer>
  );
};
